import type {ComponentType} from 'react';

type CourseManagementRouteModule = {default: ComponentType};

type CourseManagementRouteLoader = {
  (): Promise<CourseManagementRouteModule>;
  isReady: () => boolean;
};

const createRouteLoader = (
  importRoute: () => Promise<CourseManagementRouteModule>,
): CourseManagementRouteLoader => {
  let pending: Promise<CourseManagementRouteModule> | null = null;
  let ready = false;

  const load = (() => {
    if (!pending) {
      pending = importRoute().then(
        module => {
          ready = true;
          return module;
        },
        error => {
          pending = null;
          throw error;
        },
      );
    }
    return pending;
  }) as CourseManagementRouteLoader;
  load.isReady = () => ready;
  return load;
};

export const courseManagementRouteLoaders = {
  overview: createRouteLoader(() => import('Screens/TrainingManagement')),
  createSchedule: createRouteLoader(() => import('Screens/TrainingManagement/CreateSchedule')),
  instructors: createRouteLoader(() => import('Screens/InstructorManagement')),
  eventDetails: createRouteLoader(() => import('Screens/EventDetails')),
} as const;

export const getCourseManagementRouteLoader = (destination: string): CourseManagementRouteLoader => {
  const pathname = destination.split(/[?#]/, 1)[0].replace(/\/$/, '') || '/';
  if (pathname === '/course-management/schedules/new') {
    return courseManagementRouteLoaders.createSchedule;
  }
  if (pathname === '/course-management/instructors') {
    return courseManagementRouteLoaders.instructors;
  }
  if (pathname !== '/course-management' && pathname.startsWith('/course-management/')) {
    return courseManagementRouteLoaders.eventDetails;
  }
  return courseManagementRouteLoaders.overview;
};

export const isCourseManagementRouteReady = (destination: string) =>
  getCourseManagementRouteLoader(destination).isReady();

export const preloadCourseManagementRoute = (destination: string) =>
  getCourseManagementRouteLoader(destination)().then(() => undefined, () => undefined);
